import React from 'react';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';

/**
 * Full-page status screens rendered instead of the app shell, while the app is
 * still deciding whether it can run at all (authenticating, bootstrapping) or
 * has decided it cannot.
 *
 * They are grouped here because they share one contract: a single
 * `langhub-status` element carrying the ARIA live-region role that matches the
 * state. Restating that contract at each call site is how announcement
 * semantics drift — an `alert` becoming a silent `status`, or two of them
 * rendering the same id.
 */

/** Blocking work-in-progress state; `role="status"` announces politely. */
export const LoadingStatus: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div id="langhub-status" role="status">
    <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
      {/*
        Stacked rather than laid out as inline siblings: a large spinner is
        taller than a line of text, so side by side the caption aligns to the
        spinner's baseline and reads as sitting too high.
      */}
      <SpaceBetween size="m" alignItems="center">
        <Spinner size="large" />
        <div>{children}</div>
      </SpaceBetween>
    </Box>
  </div>
);

/**
 * Blocking failure state; `role="alert"` announces assertively, which is what
 * this class of failure warrants — the app cannot continue.
 *
 * Keep the copy to a heading plus a line or two, and keep raw error text, codes
 * and other internals out of it: the console is where those belong.
 */
export const FailureStatus: React.FC<{
  heading: string;
  children: React.ReactNode;
}> = ({ heading, children }) => (
  <div id="langhub-status" role="alert">
    <h1>{heading}</h1>
    {children}
  </div>
);
